import { useState } from 'react';
import {
  useWarehouses,
  useCreateWarehouse,
  useUpdateWarehouse,
  useDeleteWarehouse,
} from '../../../hooks/useWarehouses';
import { useBranches } from '../../../hooks/useBranches';
import {
  ITEMS_PER_PAGE,
  INITIAL_WAREHOUSE_STATE,
} from '../constants/warehousesConstants';
import { filterWarehouses } from '../utils/warehousesUtils';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';

export const useWarehousesTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedWarehouse, setSelectedWarehouse] = useState(INITIAL_WAREHOUSE_STATE);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [warehouseToDelete, setWarehouseToDelete] = useState(null);

  const { data: warehouses = [], isLoading } = useWarehouses();
  const { data: branches = [] } = useBranches();
  const createWarehouseMutation = useCreateWarehouse();
  const updateWarehouseMutation = useUpdateWarehouse();
  const deleteWarehouseMutation = useDeleteWarehouse();

  const {
    data: paginatedWarehouses,
    totalCount,
    totalPages,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: warehouses,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: filterWarehouses,
  });

  const handleOpenDialog = () => {
    setSelectedWarehouse(INITIAL_WAREHOUSE_STATE);
    setEditMode(false);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedWarehouse(INITIAL_WAREHOUSE_STATE);
    setEditMode(false);
  };

  const handleEdit = (warehouse) => {
    setSelectedWarehouse(warehouse);
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleCreate = async (payload) => {
    await createWarehouseMutation.mutateAsync(payload);
  };

  const handleUpdate = async (uuid, payload) => {
    await updateWarehouseMutation.mutateAsync({ id: uuid, data: payload });
  };

  const handleDeleteRequest = (warehouse) => {
    setWarehouseToDelete(warehouse);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!warehouseToDelete) return;
    await deleteWarehouseMutation.mutateAsync(warehouseToDelete.uuid);
    setDeleteDialogOpen(false);
    setWarehouseToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setWarehouseToDelete(null);
  };

  const mutationsLoading =
    createWarehouseMutation.isPending ||
    updateWarehouseMutation.isPending ||
    deleteWarehouseMutation.isPending;

  return {
    data: paginatedWarehouses,
    branches,
    isLoading,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    dialogOpen,
    editMode,
    selectedWarehouse,
    deleteDialogOpen,
    warehouseToDelete,
    mutationsLoading,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleEdit,
    handleCreate,
    handleUpdate,
    handleDeleteRequest,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
